/**
 * useProductManagement Hook
 *
 * Handles product add/remove API operations with toast notifications.
 * Used by AddProductModal for managing tracked products.
 *
 * Uses the products service layer for API operations.
 * @see ../../services/products for API implementation
 */

import { useState, useCallback } from 'react';
import { useRevalidator } from 'react-router';
import { useToastHelpers } from '../../components/feedback/ToastProvider';
import * as productsApi from '../../services/products';
import { ProductApiError } from '../../services/products';

/**
 * Input parameters for useProductManagement
 */
export interface UseProductManagementInput {
  /** Callback when products are successfully saved */
  onSuccess?: () => void;
}

/**
 * Threshold settings for products
 */
export interface ProductThresholds {
  urgentDays: number;
  warningDays: number;
}

/**
 * Return type for useProductManagement hook
 */
export interface UseProductManagementReturn {
  /** Handler for saving product changes (add/remove) */
  handleSaveProducts: (
    toAdd: string[],
    toRemove: string[],
    thresholds?: ProductThresholds
  ) => Promise<void>;
  /** Loading state while saving */
  isSaving: boolean;
}

/**
 * Hook for managing product add/remove operations
 *
 * Handles API calls for adding new products and removing existing ones,
 * with appropriate toast notifications and revalidation.
 *
 * @param input - Optional success callback
 * @returns Save handler and loading state
 */
export function useProductManagement(
  input?: UseProductManagementInput
): UseProductManagementReturn {
  const { onSuccess } = input || {};
  const revalidator = useRevalidator();
  const toast = useToastHelpers();
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveProducts = useCallback(
    async (toAdd: string[], toRemove: string[], thresholds?: ProductThresholds) => {
      setIsSaving(true);

      try {
        const { added, removed } = await productsApi.saveProducts(toAdd, toRemove, thresholds);

        // Success message
        const messages: string[] = [];
        if (added > 0) {
          messages.push(`Added ${added} product${added !== 1 ? 's' : ''}`);
        }
        if (removed > 0) {
          messages.push(`Removed ${removed} product${removed !== 1 ? 's' : ''}`);
        }
        toast.success(messages.join(', '));

        // Revalidate loader data
        revalidator.revalidate();

        // Call success callback
        onSuccess?.();
      } catch (error) {
        // Show error toast
        toast.error(error instanceof ProductApiError ? error.message : 'Failed to save products');
      } finally {
        setIsSaving(false);
      }
    },
    [toast, revalidator, onSuccess]
  );

  return { handleSaveProducts, isSaving };
}
